import { trickVariety } from './arena-goals';
import type { RideState, RideTrack } from './model';
import { medalFor, paceRatio, raceTime, timeStarsFor } from './rewards';
export { medalFor, timeStarsFor } from './rewards';

export const COMPETITION_VERSION = 3;
export const AVATARS = [
  { id: 'fox', name: 'Trail fox', symbol: '🦊', color: '#ffab7d' },
  { id: 'panther', name: 'Night cat', symbol: '🐈‍⬛', color: '#c4b0ff' },
  { id: 'frog', name: 'Hop legend', symbol: '🐸', color: '#d7f772' },
  { id: 'robot', name: 'Cadence bot', symbol: '🤖', color: '#9ee4ea' },
  { id: 'tiger', name: 'Sprint tiger', symbol: '🐯', color: '#ffd36c' },
  { id: 'alien', name: 'Space cadet', symbol: '👽', color: '#b7dfc2' },
] as const;
export type AvatarId = typeof AVATARS[number]['id'];
export type BoardMetric = 'time' | 'score' | 'games' | 'weighted';
export interface Player { id: string; name: string; avatar: AvatarId }
export interface LeaderboardRow extends Player { rank: number; value: number; games: number; medal?: string; trackId?: string }
export interface Leaderboard { metric: BoardMetric; scope: string; rows: LeaderboardRow[]; you: LeaderboardRow | null }
export const weightFor = (track: RideTrack) => track.difficultyWeight ?? 1;

const bounded = (value: number) => Math.max(0, Math.min(1, value));
/** Four independent components. Medals and achievement stars add no score points. */
export function scoreRide(track: RideTrack, s: RideState) {
  const time = raceTime(track, s.elapsed, s.trickScore);
  const medal = s.finished ? medalFor(s.position, s.rivals.length) : 'finisher';
  const flow = Math.round((.7 * bounded(s.gates / 20) + .3 * bounded(s.maxFlow / 20)) * 2500);
  const techniqueRatio = track.discipline === 'bmx'
    ? .5 * bounded(s.trickScore / 3000) + .35 * bounded(trickVariety(s.trickTypes) / 6) + .15 * bounded(s.tricksLanded / 6)
    : track.discipline === 'road'
      ? bounded(s.draftDistance / (track.length * .25) + s.leadDistance / (track.length * .6))
      : track.technical?.length
        ? bounded(s.technicalCleared / track.technical.length) * bounded(1 - s.technicalMistakes / (track.technical.length * 2))
        : bounded(s.distance / track.length) * bounded(1 - s.crashes * .25);
  const technique = Math.round(techniqueRatio * 3000);
  // Raw BMX pace prevents its trick time bonus from being counted again here.
  const pace = Math.round(paceRatio(track, s.elapsed) * 3000);
  const clean = Math.round(bounded(1 - Math.max(0, s.hits - s.crashes) * .08 - s.crashes * .25) * 1500);
  const total = flow + technique + pace + clean;
  return { total, earned: flow + technique, weighted: Math.round(total * weightFor(track)), time, medal,
    timeStars: s.finished ? timeStarsFor(track, time) : 0,
    parts: [{ label: 'Flow gates', value: flow, max: 2500 },
      { label: track.discipline === 'bmx' ? 'Trick technique' : track.discipline === 'road' ? 'Racecraft' : track.technical?.length ? 'Trail technique' : 'Bike control', value: technique, max: 3000 },
      { label: 'Riding pace', value: pace, max: 3000 }, { label: 'Clean riding', value: clean, max: 1500 }] };
}
